
import {throwError as observableThrowError,  Observable ,  Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';

import { environment } from 'environments/environment';
import DesktopConfig from 'modules/desktop/services/config';

import { ApiGatewayHelperService } from './../../app/services/apigateway-helper.service';

import { Toc } from './../interfaces/toc';
import { AppHelperService } from './../../app/services/app-helper.service';
import { GroupHelperService } from './groups-helper.service';
import { ProductViewHelperService } from './product-view-helper.service';
import { AuthService } from './../../app/services/auth.service';
import { ProductContentHelper, ContentHelper } from 'libs-content-helper';
import { StudentBookService as DesktopStudentBookService } from 'modules/desktop/services/student-book.service';
import { DownloadHelperService } from 'modules/desktop/services/download-helper.service';
import { EbookHelperService } from 'modules/desktop/services/ebook-helper.service';
import { XAPIHelperService } from './xAPI-helper.service';
import { nanoid } from 'nanoid';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { LODBHelperService } from './lo-db-helper.service';
import { CreateUserAnalyticsService } from 'modules/app/services/create-user-analytics.service';

@Injectable()
export class ProductViewDataService {
    private productContentHelper: any;

    constructor(
        private http: HttpClient,
        private apiGatewayHelperService: ApiGatewayHelperService,
        private appHelperService: AppHelperService,
        private groupHelperService: GroupHelperService,
        private productViewHelperService: ProductViewHelperService,
        private authService: AuthService,
        private desktopStudentBookService: DesktopStudentBookService,
        private downloadHelperService: DownloadHelperService,
        private ebookHelperService: EbookHelperService,
		private xAPIHelperService: XAPIHelperService,
        private networkService: NetwordDetectionService,
        private lODBHelperService: LODBHelperService,
        private createUserAnalyticsService: CreateUserAnalyticsService

    ) { 
        this.networkService.isOnline().then(response => {
            this.isOnline = response;

        });

        if (!this.networkStatusSubscription) {
            this.networkStatusSubscription = this.networkService.monitorStatus().subscribe((isOnline) => {
                this.isOnline = isOnline;
            });
        }
    }

    networkStatusSubscription: Subscription;
    isOnline: boolean;

    pendingCalls = [];

    loStatementsRetryConfig = {};
    
    //Fetch the class/product data from API and store in sharedClassProductViewData
    fetchAndStoreClassProductViewData(productCode, { classId, assignmentId, unitCode, itemCode, isLinkedBookDownloaded = false, linkedBookCode = undefined }): Observable<any> {

        let url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + 'student/' + productCode;

        let downloadedPEDetails;
        if (environment.isDesktopApp) {
            // adding mobile gateway url as basepath if accessing activity inside desktop app
            url = DesktopConfig.app.api.baseUrl + url;
            let peDownloadedList = this.desktopStudentBookService.peDownloadedList;
            downloadedPEDetails = peDownloadedList.get(productCode);
        }

        if (classId) {
            url += '/' + classId;
            if (assignmentId) {
                url += '/' + assignmentId;
            } else if (unitCode) {
                url += '?unitCode=' + unitCode;
            }

        }


        if ((downloadedPEDetails && isLinkedBookDownloaded) && !this.downloadHelperService.autoUpdateStatus[productCode]) {
            let peProductUmbrella = this.desktopStudentBookService.getProductInfo(productCode);
            let linkedDownloadedBooks = downloadedPEDetails.linkedEBooks;
            let peDownloadPath = downloadedPEDetails.location;
            let flatJsonPath = peDownloadPath + 'meta\\definition_flat.json';
            let apiPEMeta = this.ebookHelperService.getPEMetaFromLinkedBook(productCode, linkedBookCode, linkedDownloadedBooks[0]);
            console.log('apiPEMeta', apiPEMeta);
            apiPEMeta = apiPEMeta || {};

            return this.http.get(flatJsonPath).pipe(map((flattenedJSON: any) => {

                let bundleCode = peProductUmbrella.productUmbrella['bundle-code'];
                let res: any = {
                    meta: {
                        productcode: productCode,
                        meta: apiPEMeta,
                    },
                    toc: {
                        result: flattenedJSON.items.default
                    },
                    bundleDetails: peProductUmbrella.productUmbrella,
                    isPEDownloaded: true
                }
                res.meta.bundles = {};
                res.meta.bundles[bundleCode] = peProductUmbrella.productUmbrella;



                if (res && res.meta && res.meta.meta) {
                    res.meta.meta.paths = {
                        "public-assets": `${peDownloadPath}/assets`,
                        "json-flattened": `${peDownloadPath}/meta/definition_flat.json`
                    }
                }
                res.groupData ? this.groupHelperService.setGroups(res.groupData.entities) : '';
                let itemCodeMap = this.getCompleteItemCodeMap(res.toc.result, itemCode, {});
                res.itemCode = itemCodeMap[itemCode];
                return this.transformTOC(res, { unitCode });
            })).pipe(catchError((error) => {
                this.appHelperService.navigateToDashboardApp([]);
                return observableThrowError(error);
            }));

        } else {
            return this.http.get(url).pipe(map((res: any) => {
                if (res && res.success === false && res.error == 'UA_NOT_PRESENT_ERROR') {
                    this.createUserAnalyticsService.setUserInfo(res.userId, res.orgId, res.pubnubEventType);
                    return res;
                }

                res.groupData ? this.groupHelperService.setGroups(res.groupData.entities) : '';
                let itemCodeMap = this.getCompleteItemCodeMap(res.toc.result, itemCode, {});
                res.itemCode = itemCodeMap[itemCode];
                return this.transformTOC(res, { unitCode });
            })).pipe(catchError((error) => {
                this.appHelperService.navigateToDashboardApp([]);
                return observableThrowError(error);
            }));

        }

    }

    fetchAndStoreProductViewData({ productCode, classId, unitCode = undefined, itemCode = undefined, isLinkedBookDownloaded = false, linkedBookCode = undefined }, assignmentDataRequired = false, getLearnerUA = false): Observable<any> {
        let url;

        url = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true) + 'teacher/' + productCode;
        let downloadedPEDetails

        if (environment.isDesktopApp) {
            // adding mobile gateway url as basepath if accessing activity inside desktop app
            url = DesktopConfig.app.api.baseUrl + url;
            let peDownloadedList = this.desktopStudentBookService.peDownloadedList;
            downloadedPEDetails = peDownloadedList.get(productCode);
        }

        if (classId) {
            url += `/${classId}`;
        }


        if ((downloadedPEDetails && isLinkedBookDownloaded) && !this.downloadHelperService.autoUpdateStatus[productCode]) {
            let peProductUmbrella = this.desktopStudentBookService.getProductInfo(productCode);
            let linkedDownloadedBooks = downloadedPEDetails.linkedEBooks;
            let peDownloadPath = downloadedPEDetails.location;
            let flatJsonPath = peDownloadPath + 'meta\\definition_flat.json';
            let apiPEMeta = this.ebookHelperService.getPEMetaFromLinkedBook(productCode, linkedBookCode, linkedDownloadedBooks[0]);
            console.log('apiPEMeta', apiPEMeta);
            apiPEMeta = apiPEMeta || {};

            return this.http.get(flatJsonPath).pipe(map((flattenedJSON: any) => {

                let bundleCode = peProductUmbrella.productUmbrella['bundle-code'];
                let res: any = {
                    meta: {
                        productcode: productCode,
                        meta: apiPEMeta,
                    },
                    toc: {
                        result: flattenedJSON.items.default
                    },
                    bundleDetails: peProductUmbrella.productUmbrella
                }
                res.meta.bundles = {};
                res.meta.bundles[bundleCode] = peProductUmbrella.productUmbrella;



                if (res && res.meta && res.meta.meta) {
                    res.meta.meta.paths = {
                        "public-assets": `${peDownloadPath}/assets`,
                        "json-flattened": `${peDownloadPath}/meta/definition_flat.json`
                    }
                }
                let itemCodeMap = this.getCompleteItemCodeMap(res.toc.result, itemCode, {});
                res.itemCode = itemCodeMap[itemCode];
                // assignmentDataRequired is true when teacher is creating assignments,
                // so we can use it to hide teacher activities while creating assignments
                return this.transformTOC(res, { prepareTocForAssignments: assignmentDataRequired, unitCode });
            })).pipe(catchError((error) => {
                this.appHelperService.navigateToDashboardApp([]);
                return observableThrowError(error);
            }));

        } else {
            return this.http.get(url).pipe(map((res: any) => {
                if (res && res.success === false) {
                    return res;
                }
                let itemCodeMap = this.getCompleteItemCodeMap(res.toc.result, itemCode, {});
                res.itemCode = itemCodeMap[itemCode];
                // assignmentDataRequired is true when teacher is creating assignments,
                // so we can use it to hide teacher activities while creating assignments
                return this.transformTOC(res, { prepareTocForAssignments: assignmentDataRequired, unitCode });
            })).pipe(catchError((error) => {
                this.appHelperService.navigateToDashboardApp([]);
                return observableThrowError(error);
            }));
        }


    }

    //Transfrom the Toc from Array to Object
    /*
     For transforming TOC we have copied the references in tocObject, tocArray , navigationMeta Array.
     This is done to sync all the data between these data Objects i.e if data is updated in tocObject
     it will be updated in tocArray , navigationMeta as well. The cached response in ProductViewDataService
     service is also updated if the data is changed in component.
     */
    transformTOC(classProductViewData, { prepareTocForAssignments = false, unitCode = '' } = {}) {
        /* userData is needed to detrmine if user is a student, if yes then filter activities with role as teacher */
        const userData = this.authService.getUserData();

        let toc: Toc = {
            'tocObject': {},
            'tocArray': classProductViewData["toc"],
            'navigationMeta': []
        };
        /* Fetching bundleProduct to initialize the library functions */
        if (classProductViewData && classProductViewData.bundleDetails) {
            const productCode = classProductViewData.meta['productcode'];
            const bundleProduct = classProductViewData.bundleDetails.products.find((bP) => bP['dls-product']['code'] === productCode);

            this.productContentHelper = new ProductContentHelper(bundleProduct);
        }

        let tocItemObject = {};

        toc.tocArray.result = toc.tocArray.result.filter((obj, index) => {
            // Set sequence for Parent/Root/Unit Node only
            obj.sequence = index + 1;
            if (unitCode && obj['item-code'] !== unitCode) {
                return false;
            }
            tocItemObject = this.transformItem(obj, null, toc['navigationMeta'], prepareTocForAssignments || (userData && userData.role === 'student'));
            tocItemObject[obj['item-code']].index = index;
            toc['tocObject'][obj['item-code']] = tocItemObject[obj['item-code']];
            return true;
        });

        this.productViewHelperService.setNavigationMeta(toc.navigationMeta);
        classProductViewData.toc = toc;
        return classProductViewData;
    }

    //Transfrom particular Toc Item from Array to Object
    transformItem(object, parent, tocNavigationMeta, isLearner = false) {
        let transformedOject = {};
        transformedOject[object['item-code']] = {};
        Object.keys(object).forEach((prop) => {
            transformedOject[object['item-code']][prop] = object[prop];
        });
        transformedOject[object['item-code']]['parent-name'] = parent;
        if (object.items) {
            transformedOject[object['item-code']].items = {};
            object.items = object.items.filter((item, index) => {
                // Filter teacher only activity if student 
                if ((item['item-type'] === 'resource') && (isLearner && ContentHelper.isTeacherOnlyContent(item))) {
                    return false;
                }
                const parentName = (transformedOject[object['item-code']]['parent-name'] ? transformedOject[object['item-code']]['parent-name'] + ' > ' : '') + object.name;
                transformedOject[object['item-code']].items[item['item-code']] = this.transformItem(item, parentName, tocNavigationMeta, isLearner)[item['item-code']];
                transformedOject[object['item-code']].items[item['item-code']].index = index;
                return true;
            });
        }

        if (transformedOject[object['item-code']]['item-type'] === 'resource') {
            tocNavigationMeta.push({
                'item-code': transformedOject[object['item-code']]['item-code'],
                '__analytics': transformedOject[object['item-code']]['__analytics'],
                'sub-type': transformedOject[object['item-code']]['sub-type']
            });
            transformedOject[object['item-code']].lchObject = object.lchObject = {
                iconObj: this.productContentHelper.getStatusIconForContent(object),
                scoresObj: this.productContentHelper.getFormattedScore(object)
            }
        }
        return transformedOject;
    }

    getCompleteItemCodeMap(TOCResult, unitCode, itemCodeMap) {

        TOCResult.forEach(element => {
            if (element.items) {
                this.getCompleteItemCodeMap(element.items, unitCode, itemCodeMap)
            } else {
                let splitCodesArray = element['item-code'].split('/');
                let splitCodesArrayLength = splitCodesArray.length;
                itemCodeMap[splitCodesArray[splitCodesArrayLength - 1]] = element['item-code'];
            }
        });

        return itemCodeMap;

    }

    getProductContentHelper() {
        return this.productContentHelper;
    }

    getAnalyticsOfItem(productCode, classId, itemId, groupId): Observable<any> {
        let routePrefix = this.apiGatewayHelperService.getApiGatewayPrefixUrl(true);
        if (environment.isDesktopApp) {
            routePrefix = DesktopConfig.app.api.baseUrl + routePrefix;
        }
        let url = routePrefix + 'product/' + productCode + '/class/' + classId + '/item/' + itemId + '?history=true';
        if (groupId) {
            url += '&groupId=' + groupId;
        }
        return this.http.get(url).pipe(
            map(res => {
                return res;
            })
        )
    }

    /**
     * function to push LO statements from db to server.
     * @param givenUserId - gigya user id of user.
     * @param givenPeCode - peCode that needs to be synced.
     * @param updateVM - should VM be updated after sync
     */
    async syncLOStatementsFromLS(givenUserId, givenPeCode, updateVM = false) {

        // do not sync if offline.
        if (!this.isOnline) {
            return;
        }

        // get statements from DB.
        let statements = await this.lODBHelperService.getStatementsFromDB(givenUserId, givenPeCode);

        let syncArray = [];
        let syncedLOIds = [];
        let promises = [];

        // Generate an array of Objects that contain the data per space which needs to be synced.
        Object.keys(statements).forEach(uniqueLOId => {
            Object.keys(statements[uniqueLOId]).forEach(space => {
                if (statements[uniqueLOId][space].metadata.folderType == 'space') {
                    Object.keys(statements[uniqueLOId][space]).forEach(classs => {
                        if (classs == 'metadata') {
                            return;
                        }
                        syncArray.push(statements[uniqueLOId][space][classs]);
                    });
                } else {
                    syncArray.push(statements[uniqueLOId][space]);
                }
            });
        });

        const userData = this.authService.getUserData();

        // for each object that contains the statements of individual space and class
        // create a new xapi object.
        // filter out statements whose sync is pending.
        // add the object to a promise.
        for (let syncObject of syncArray) {

            let spaceId = syncObject.metadata.spaceId;
            let classId = syncObject.metadata.classId;
            let productCode = syncObject.metadata.productCode;

            // do not sync for non current user.
            if (userData.extUserId != givenUserId) {
                return;
            }

            let routePrefix = this.apiGatewayHelperService.getApiGatewayPrefixUrl();
            if (environment.isDesktopApp) {
                routePrefix = DesktopConfig.app.api.baseUrl + routePrefix;
            }
            var serverRoutePrefix = routePrefix.substr(0, routePrefix.length - 1);

            let analyticsDataObj = {
                serverRoutePrefix: serverRoutePrefix,
                dlsProductCode: productCode,
                spaceId: spaceId,
                dlsClassId: classId,
                userId: givenUserId
            };

            let options = {
                returnInstance: true,
                eventHandlers: {
                    errorCallback: (errorSource, error) => {
                        this.retryLOStatementsSync(givenUserId, givenPeCode, updateVM);
                        syncObject.data.forEach(statement => {
                            this.pendingCalls.splice(this.pendingCalls.indexOf(statement.json.meta.uniqueStatementId), 1);
                        });

                    },
                    successCallback: (successSource, response) => {
                        console.log('success');
                    }
                }
            }

            //Setup event handlers for receiving events from LO.
            let xapiLOAnalytics = this.xAPIHelperService.setXAPIAnalyticsObject(analyticsDataObj, options);


            syncObject.data = syncObject.data.filter(statement => {
                return this.pendingCalls.indexOf(statement.json.meta.uniqueStatementId) == -1;
            });

            syncObject.data.forEach(statement => {
                this.pendingCalls.push(statement.json.meta.uniqueStatementId);
            });

            if (syncObject.data.length) {
                promises.push(xapiLOAnalytics.postStatementsNoBatch(syncObject.data, { notifyViaCallback: true }));
            }

        }

        // if any promise is present.
        // wait for them to resolve.
        // remove from pending calls array.
        // add in synced calls array.
        if (promises.length) {

            let responses = await Promise.all(promises);

            responses.forEach(response => {

                if(response) {
                    response.entities.forEach(statement => {
                        syncedLOIds.push(statement.meta.uniqueStatementId);
                    });
                }
                    
            });
        }

        // if any statents were synced.
        // get statemtents from DB. this is done as user may have added new statements in DB.
        // filter out all the synced statements from this object.
        // add this object in DB.
        if (syncedLOIds.length) {

            let newStatements = await this.lODBHelperService.getStatementsFromDB(givenUserId, givenPeCode);

            Object.keys(newStatements).forEach(uniqueLOId => {
                Object.keys(newStatements[uniqueLOId]).forEach(space => {
                    if (newStatements[uniqueLOId][space].metadata.folderType == 'space') {
                        Object.keys(newStatements[uniqueLOId][space]).forEach(classs => {
                            if (classs == 'metadata') {
                                return;
                            }

                            newStatements[uniqueLOId][space][classs].data = newStatements[uniqueLOId][space][classs].data.filter(statement => {
                                return syncedLOIds.indexOf(statement.json.meta.uniqueStatementId) == -1;
                            });

                            if (newStatements[uniqueLOId][space][classs].data.length == 0) {
                                delete newStatements[uniqueLOId][space][classs];
                            }

                        });
                    } else {
                        newStatements[uniqueLOId][space].data = newStatements[uniqueLOId][space].data.filter(statement => {
                            return syncedLOIds.indexOf(statement.json.meta.uniqueStatementId) == -1;
                        });

                        if (newStatements[uniqueLOId][space].data.length == 0) {
                            delete newStatements[uniqueLOId][space];
                        }

                    }
                });
            });

            await this.lODBHelperService.putVMinDB(givenUserId, givenPeCode, 'lo_statements', newStatements, updateVM);

        }


    }

    /**
     * function to retry sync of current PE in case there was an error. 
     * @param givenUserId - gigya user id of user.
     * @param givenPeCode - peCode that needs to be synced.
     * @param updateVM - should VM be updated after sync
     */
    retryLOStatementsSync(givenUserId, givenPeCode, updateVM = false) {
        let uniqueKey = `${givenUserId}~~${givenPeCode}`;
        if (!this.loStatementsRetryConfig[uniqueKey]) {
            this.loStatementsRetryConfig[uniqueKey] = {
                timeoutId: undefined,
                timeoutTime: 1
            }
        }

        if (!this.loStatementsRetryConfig[uniqueKey].timeoutId) {

            this.loStatementsRetryConfig[uniqueKey].timeoutTime = this.nextFibonacci(this.loStatementsRetryConfig[uniqueKey].timeoutTime);

            this.loStatementsRetryConfig[uniqueKey].timeoutId = setTimeout(() => {
                clearTimeout(this.loStatementsRetryConfig[uniqueKey].timeoutId);
                this.loStatementsRetryConfig[uniqueKey].timeoutId = undefined;

                this.syncLOStatementsFromLS(givenUserId, givenPeCode, updateVM);
            },this.loStatementsRetryConfig[uniqueKey].timeoutTime);

        }

    }

    // get an approx value of next fibbo number from only current number.
    nextFibonacci(n) {
        let a = n * (1 + Math.sqrt(5)) / 2.0;
        return Math.round(a);
    }


}